import type { QueryKey } from '@tanstack/react-query';

import {
  apiListQuery,
  apiQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
  type ApiMutationResult,
} from '@/shared/data';

import * as api from './communication-setting.api';
import type {
  CommunicationSettingCreateBody,
  CommunicationSettingUpdateBody,
} from './communication-setting.wire';

// Cache e mutações da Régua de comunicação (shared/data). Sem texto de UI nem toast (X06): o feedback
// é da tela. Um hook genérico de mutação por módulo (X07).

export const communicationSettingKeys = createQueryKeys('communication-setting');

// A lista é lida pela fonte `communicationSetting.list` (communication-setting.sources.ts), com a chave
// `lists()`: sem `tenant` no fio (o legado não manda), a chave também não leva.

export const communicationSettingDetailQuery = (id: string) =>
  apiQuery(
    communicationSettingKeys.detail(id),
    ({ signal }) => api.getCommunicationSetting(id, signal),
    { enabled: id !== '' && id !== 'novo' },
  );

/** Opções do autocomplete de régua (a porta que outros módulos usam, U06). */
export const communicationSettingOptionsQuery = (
  params: { walletsCode?: string | number | null | undefined; searchString?: string | undefined },
  extra: { enabled?: boolean } = {},
) =>
  apiListQuery(
    communicationSettingKeys.custom('options', params),
    ({ signal }) => api.listCommunicationSettingOptions(params, signal),
    extra,
  );

interface MutationDef<V, D> {
  readonly run: (variables: V) => Promise<D>;
  readonly invalidate: (variables: V) => readonly QueryKey[];
}

const onModule = <V, D>(run: (variables: V) => Promise<D>): MutationDef<V, D> => ({
  run,
  invalidate: () => [communicationSettingKeys.all],
});

/** Uma entrada por chamada de escrita do fio. */
export const communicationSettingMutations = {
  create: onModule((body: CommunicationSettingCreateBody) => api.createCommunicationSetting(body)),
  update: onModule(({ id, body }: { id: string; body: CommunicationSettingUpdateBody }) =>
    api.updateCommunicationSetting(id, body),
  ),
  delete: onModule(({ id }: { id: string }) => api.deleteCommunicationSetting(id)),
} as const;

export type CommunicationSettingMutationName = keyof typeof communicationSettingMutations;
type Def<K extends CommunicationSettingMutationName> = (typeof communicationSettingMutations)[K];
export type CommunicationSettingMutationVariables<K extends CommunicationSettingMutationName> =
  Parameters<Def<K>['run']>[0];
export type CommunicationSettingMutationData<K extends CommunicationSettingMutationName> = Awaited<
  ReturnType<Def<K>['run']>
>;

/**
 * Mutação de uma chamada de escrita, invalidando o módulo depois do sucesso. Sem toast por padrão
 * (`error: false`): a tela mostra o sucesso e decide onde o erro aparece (X06, X11).
 */
export function useCommunicationSettingMutation<K extends CommunicationSettingMutationName>(
  name: K,
  options: Omit<
    ApiMutationOptions<
      CommunicationSettingMutationData<K>,
      CommunicationSettingMutationVariables<K>
    >,
    'mutationFn'
  > = {},
): ApiMutationResult<
  CommunicationSettingMutationData<K>,
  CommunicationSettingMutationVariables<K>
> {
  const def = communicationSettingMutations[name] as unknown as MutationDef<
    CommunicationSettingMutationVariables<K>,
    CommunicationSettingMutationData<K>
  >;
  return useApiMutation<
    CommunicationSettingMutationData<K>,
    CommunicationSettingMutationVariables<K>
  >({
    mutationKey: ['communication-setting', 'mutation', name],
    invalidate: (_data, variables) => def.invalidate(variables),
    error: false,
    ...options,
    mutationFn: def.run,
  });
}

export const communicationSettingQuery = (id: string | null | undefined) =>
  communicationSettingDetailQuery(id ?? '');
